import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Heart, Trash2, Search, ChevronUp, ChevronDown } from 'lucide-react';
import api from '../utils/api';
import { formatDateBR, todayLocalISO } from '../utils/formatDate';
import toast from 'react-hot-toast';
import AnimalSearchSelect from '../components/AnimalSearchSelect';

const RESULTADO_BADGE = { pendente:'badge-yellow', prenhe:'badge-green', vazia:'badge-red', repetiu:'badge-orange' };

export default function Coberturas() {
  const [data, setData] = useState([]);
  const [femeas, setFemeas] = useState([]);
  const [machos, setMachos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editandoResultado, setEditandoResultado] = useState(null);
  const [busca, setBusca] = useState('');
  const [filtroResultado, setFiltroResultado] = useState('');
  const [sort, setSort] = useState({ col: 'data', dir: 'desc' });
  const [form, setForm] = useState({ femea_id:'', macho_id:'', data:todayLocalISO(), tipo:'natural', lote_monta:'', resultado:'pendente', observacoes:'' });
  const [formResult, setFormResult] = useState({ resultado:'', data_diagnostico:'', metodo_diagnostico:'', observacoes:'' });

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data:d } = await api.get('/reproducao/coberturas'); setData(d); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/animais', { params:{ sexo:'F', situacao:'ativo', limit:500 } }).then(r => setFemeas(r.data.data || r.data));
    api.get('/animais', { params:{ sexo:'M', situacao:'ativo', limit:500 } }).then(r => setMachos(r.data.data || r.data));
  }, []);

  const salvar = async () => {
    try { await api.post('/reproducao/coberturas', form); toast.success('Cobertura registrada!'); setShowModal(false); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Erro'); }
  };

  const atualizarResultado = async () => {
    try { await api.put(`/reproducao/coberturas/${editandoResultado.id}`, formResult); toast.success('Resultado atualizado!'); setEditandoResultado(null); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Erro'); }
  };

  const excluir = async (id) => {
    if (!window.confirm('Excluir esta cobertura? O parto previsto associado também será removido.')) return;
    try { await api.delete(`/reproducao/coberturas/${id}`); toast.success('Cobertura excluída'); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Erro'); }
  };

  const toggleSort = (col) => setSort(s => ({ col, dir: s.col === col && s.dir === 'asc' ? 'desc' : 'asc' }));
  const SortIcon = ({ col }) => sort.col === col
    ? (sort.dir === 'asc' ? <ChevronUp size={13}/> : <ChevronDown size={13}/>)
    : <ChevronDown size={13} style={{ opacity:0.3 }}/>;

  const filtrado = data
    .filter(d => {
      if (filtroResultado && d.resultado !== filtroResultado) return false;
      if (busca) {
        const t = busca.toLowerCase();
        return (d.femea_brinco||'').toLowerCase().includes(t) ||
               (d.femea_nome||'').toLowerCase().includes(t) ||
               (d.macho_brinco||'').toLowerCase().includes(t);
      }
      return true;
    })
    .sort((a, b) => {
      let va = a[sort.col] ?? ''; let vb = b[sort.col] ?? '';
      const dir = sort.dir === 'asc' ? 1 : -1;
      if (sort.col === 'parto_previsto' || sort.col === 'data') return (new Date(va||0) - new Date(vb||0)) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });

  const pendentes = data.filter(d => d.resultado === 'pendente').length;
  const prenhas   = data.filter(d => d.resultado === 'prenhe').length;

  return (
    <div>
      <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div><h1>Estação de Monta</h1><p>{data.length} coberturas · {pendentes} pendentes · {prenhas} prenhas</p></div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}><Plus size={16} /> Registrar Cobertura</button>
      </div>

      <div className="stats-grid" style={{ gridTemplateColumns:'repeat(4,1fr)' }}>
        {[['Pendentes',pendentes,'#D97706'],['Prenhas',prenhas,'#16A34A'],['Vazias',data.filter(d=>d.resultado==='vazia').length,'#DC2626'],['Repetiram',data.filter(d=>d.resultado==='repetiu').length,'#EA580C']].map(([label,val,cor]) => (
          <div key={label} className="stat-card" style={{ borderLeft:`4px solid ${cor}` }}>
            <div className="stat-label">{label}</div>
            <div className="stat-value" style={{ color:cor }}>{val}</div>
          </div>
        ))}
      </div>

      <div className="card" style={{ marginBottom:16 }}>
        <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
          <div className="search-box" style={{ flex:2, minWidth:200 }}>
            <Search size={16}/>
            <input placeholder="Buscar fêmea ou reprodutor..." value={busca} onChange={e => setBusca(e.target.value)}/>
          </div>
          <select value={filtroResultado} onChange={e => setFiltroResultado(e.target.value)} style={{ width:'auto' }}>
            <option value="">Todos os resultados</option>
            <option value="pendente">Pendente</option>
            <option value="prenhe">Prenhe</option>
            <option value="vazia">Vazia</option>
            <option value="repetiu">Repetiu cio</option>
          </select>
        </div>
      </div>

      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        <table>
          <thead><tr>
            {[['femea_brinco','Fêmea'],['macho_brinco','Reprodutor'],['data','Data'],['tipo','Tipo'],['parto_previsto','Parto Previsto'],['resultado','Resultado']].map(([col,label]) => (
              <th key={col} style={{ cursor:'pointer', userSelect:'none' }} onClick={() => toggleSort(col)}>
                <span style={{ display:'inline-flex', alignItems:'center', gap:4 }}>{label} <SortIcon col={col}/></span>
              </th>
            ))}
            <th></th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={7} style={{ textAlign:'center', padding:40 }}>Carregando...</td></tr>
            : filtrado.length === 0 ? <tr><td colSpan={7}><div className="empty-state"><p>Nenhuma cobertura encontrada</p></div></td></tr>
            : filtrado.map(d => (
              <tr key={d.id}>
                <td><strong style={{ color:'var(--color-primary)' }}>{d.femea_brinco}</strong>{d.femea_nome && ` — ${d.femea_nome}`}</td>
                <td>{d.macho_brinco}{d.macho_nome && ` — ${d.macho_nome}`}</td>
                <td>{formatDateBR(d.data)}</td>
                <td><span className="badge badge-blue">{d.tipo}</span></td>
                <td style={{ fontSize:13 }}>{d.resultado==='prenhe' && d.parto_previsto ? formatDateBR(d.parto_previsto) : '—'}</td>
                <td><span className={`badge ${RESULTADO_BADGE[d.resultado]||'badge-gray'}`}>{d.resultado}</span></td>
                <td style={{ whiteSpace:'nowrap' }}>
                  <div style={{ display:'flex', gap:4 }}>
                    {d.resultado === 'pendente' && (
                      <button className="btn btn-secondary btn-sm" onClick={() => { setEditandoResultado(d); setFormResult({ resultado:'pendente', data_diagnostico:'', metodo_diagnostico:'', observacoes:'' }); }}>
                        Diagnóstico
                      </button>
                    )}
                    <button className="btn btn-secondary btn-sm" onClick={() => excluir(d.id)} style={{ color:'#DC2626', padding:'4px 8px' }}><Trash2 size={14}/></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-header"><h2>Registrar Cobertura</h2><button className="btn btn-secondary btn-sm" onClick={() => setShowModal(false)}>✕</button></div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group"><label>Fêmea *</label>
                  <AnimalSearchSelect value={form.femea_id} onChange={id => setForm(p => ({ ...p, femea_id:id }))} sexo="F" required />
                </div>
                <div className="form-group"><label>Reprodutor *</label>
                  <AnimalSearchSelect value={form.macho_id} onChange={id => setForm(p => ({ ...p, macho_id:id }))} sexo="M" required />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Data *</label><input type="date" value={form.data} onChange={e => setForm(p => ({ ...p, data:e.target.value }))}/></div>
                <div className="form-group"><label>Tipo</label>
                  <select value={form.tipo} onChange={e => setForm(p => ({ ...p, tipo:e.target.value }))}>
                    <option value="natural">Natural</option><option value="iatf">IATF</option><option value="inseminacao">Inseminação Artificial</option>
                  </select>
                </div>
              </div>
              <div className="form-group"><label>Lote de Monta</label><input value={form.lote_monta} onChange={e => setForm(p => ({ ...p, lote_monta:e.target.value }))}/></div>
              <div className="form-group"><label>Observações</label><textarea rows={2} value={form.observacoes} onChange={e => setForm(p => ({ ...p, observacoes:e.target.value }))}/></div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={salvar} disabled={!form.femea_id || !form.macho_id}>Registrar</button>
            </div>
          </div>
        </div>
      )}

      {editandoResultado && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setEditandoResultado(null)}>
          <div className="modal">
            <div className="modal-header"><h2>Diagnóstico — {editandoResultado.femea_brinco}</h2><button className="btn btn-secondary btn-sm" onClick={() => setEditandoResultado(null)}>✕</button></div>
            <div className="modal-body">
              <div style={{ padding:'10px 14px', background:'#F0FDF4', borderRadius:8, marginBottom:14, fontSize:13 }}>
                <strong>Parto previsto:</strong> {editandoResultado.parto_previsto ? formatDateBR(editandoResultado.parto_previsto) : 'será calculado ao confirmar prenhez'}
                {formResult.resultado === 'prenhe' && <span style={{ color:'#16A34A', marginLeft:8 }}>✓ Parto previsto e agenda serão criados automaticamente</span>}
              </div>
              <div className="form-group"><label>Resultado *</label>
                <select value={formResult.resultado} onChange={e => setFormResult(p => ({ ...p, resultado:e.target.value }))}>
                  <option value="pendente">Pendente</option>
                  <option value="prenhe">Prenhe ✓</option>
                  <option value="vazia">Vazia</option>
                  <option value="repetiu">Repetiu cio</option>
                </select>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Data Diagnóstico</label><input type="date" value={formResult.data_diagnostico} onChange={e => setFormResult(p => ({ ...p, data_diagnostico:e.target.value }))}/></div>
                <div className="form-group"><label>Método</label>
                  <select value={formResult.metodo_diagnostico} onChange={e => setFormResult(p => ({ ...p, metodo_diagnostico:e.target.value }))}>
                    <option value="">Selecione</option><option>Ultrassom</option><option>Palpação</option><option>Repasse do macho</option>
                  </select>
                </div>
              </div>
              <div className="form-group"><label>Observações</label><textarea rows={2} value={formResult.observacoes} onChange={e => setFormResult(p => ({ ...p, observacoes:e.target.value }))}/></div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setEditandoResultado(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={atualizarResultado}>Salvar Diagnóstico</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
